import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { error: err } = await resetPassword(email);
      if (err) {
        setError(err);
      } else {
        setSent(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center font-heading font-bold">
              TG
            </span>
            <div className="text-left">
              <div className="font-heading font-bold text-xl text-foreground-900">Reset Password</div>
              <div className="text-xs text-foreground-500">TGMCI Admin</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-background-200 p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                <i className="ri-mail-send-line text-3xl" aria-hidden="true" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-900 mb-2">Check Your Email</h2>
              <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                If an account exists for <strong className="text-foreground-700">{email}</strong>, we&apos;ve sent a password
                reset link. Follow the link in the email to set a new password.
              </p>
              <Link to="/admin/login" className="btn-primary w-full justify-center">
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                Enter the email address associated with your admin account and we&apos;ll send you a link to reset your
                password.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                    placeholder="admin@tgmci.org"
                  />
                </div>

                {error && (
                  <div className="px-3 py-2.5 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
                  {submitting ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/admin/login" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
            <i className="ri-arrow-left-line mr-1" aria-hidden="true" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}